import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { t } from 'i18next';

import { ReactComponent as AccountBalanceIcon } from 'assets/icons/AccountBalanceIcon.svg';
import { ReactComponent as CaseIcon } from 'assets/icons/CaseIcon.svg';
import { ReactComponent as DocumentFileIcon } from 'assets/icons/DocumentFileIcon.svg';

import { IBusinessPlanButtonOption } from 'interfaces/businessPlan';

import { ObjectValues } from 'helpers/types';

import scssVariables from 'styles/sass-variables.module.scss';

export const BUSINESS_PLAN_TYPES = {
  FOR_BANK: 'BANK',
  FOR_INVESTORS: 'LIGHT',
  ONE_PAGE: 'ONE_PAGE',
  ONE_PAGE_SHORT: 'ONE_PAGE_SHORT',
} as const;

export type IBusinessToolType = ObjectValues<typeof BUSINESS_PLAN_TYPES>;

export const BUSINESS_PLAN_TEMPLATE = {
  [BUSINESS_PLAN_TYPES.FOR_BANK]: t('forBank'),
  [BUSINESS_PLAN_TYPES.FOR_INVESTORS]: t('forInvestorsUp'),
  [BUSINESS_PLAN_TYPES.ONE_PAGE]: t('onePage'),
} as const;

export const BUSINESS_PLAN_STATUS = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED',
  DELETED: 'DELETED',
} as const;

export const ONE_PAGE_LINK_STATUS = {
  RESTRICTED: 'RESTRICTED',
  PUBLIC: 'PUBLIC',
} as const;

export const businessPlanOptions: IBusinessPlanButtonOption[] = [
  {
    template: BUSINESS_PLAN_TYPES.FOR_BANK,
    label: BUSINESS_PLAN_TEMPLATE[BUSINESS_PLAN_TYPES.FOR_BANK],
    icon: <AccountBalanceIcon />,
    color: scssVariables.green_default,
    borderColor: scssVariables.green_300,
    backgroundColor: scssVariables.green_100,
    backgroundColorHover: scssVariables.green_300,
  },
  {
    template: BUSINESS_PLAN_TYPES.FOR_INVESTORS,
    label: BUSINESS_PLAN_TEMPLATE[BUSINESS_PLAN_TYPES.FOR_INVESTORS],
    icon: <CaseIcon />,
    color: scssVariables.blue_default,
    borderColor: scssVariables.blue_300,
    backgroundColor: scssVariables.blue_100,
    backgroundColorHover: scssVariables.blue_300,
  },
  {
    template: BUSINESS_PLAN_TYPES.ONE_PAGE_SHORT,
    label: BUSINESS_PLAN_TEMPLATE[BUSINESS_PLAN_TYPES.ONE_PAGE],
    icon: <DocumentFileIcon />,
    color: scssVariables.orange_default,
    borderColor: scssVariables.orange_200,
    backgroundColor: scssVariables.orange_100,
    backgroundColorHover: scssVariables.orange_200,
  },
];

// Hook to provide language-reactive business plan options
export const useBusinessPlanOptions = (): IBusinessPlanButtonOption[] => {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => [
      {
        template: BUSINESS_PLAN_TYPES.FOR_BANK,
        label: t('forBank'),
        icon: <AccountBalanceIcon />,
        color: scssVariables.green_default,
        borderColor: scssVariables.green_300,
        backgroundColor: scssVariables.green_100,
        backgroundColorHover: scssVariables.green_300,
      },
      {
        template: BUSINESS_PLAN_TYPES.FOR_INVESTORS,
        label: t('forInvestorsUp'),
        icon: <CaseIcon />,
        color: scssVariables.blue_default,
        borderColor: scssVariables.blue_300,
        backgroundColor: scssVariables.blue_100,
        backgroundColorHover: scssVariables.blue_300,
      },
      {
        template: BUSINESS_PLAN_TYPES.ONE_PAGE_SHORT,
        label: t('onePage'),
        icon: <DocumentFileIcon />,
        color: scssVariables.orange_default,
        borderColor: scssVariables.orange_200,
        backgroundColor: scssVariables.orange_100,
        backgroundColorHover: scssVariables.orange_200,
      },
    ],
    [i18n.language],
  );
};
